/*
 * NZS Compare Products. See plan: Random shit\_nzs_audit\compare-tool\PLAN.md
 *
 * Step 1 (picker) does NOT rebuild the filter/drawer UI -- it fetches the real
 * /collections/{handle} page for the active category and injects its .nzs-toolbar /
 * .nzs-layout nodes (filter rail + drawer + card grid) verbatim, same technique as the
 * stock assets/product-comparison.js's loadTablet/loadMobile (fetch a page, extract nodes).
 * COUPLING: depends on sections/nzs-collection.liquid keeping these class names/ids:
 * .nzs-toolbar, .nzs-layout, [data-nzs-card], #nzs-filter-config, #nzs-subcats.
 * Only ONE category's markup is ever in the DOM at a time (teardown before each fetch) --
 * #nzs-filter-config/#nzs-subcats are fixed ids read via getElementById, so two categories'
 * markup present together would collide.
 */
(function () {
  'use strict';

  var CAT_HANDLE = {
    preworkout: 'pre-workout', protein: 'protein', creatine: 'creatine', amino: 'amino-bcaa',
    massgainer: 'mass-gainers', electrolyte: 'electrolytes', fatburner: 'weight-management',
    health: 'health-wellness', other: 'canned-energy'
  };
  var CAT_LABEL = {
    preworkout: 'Pre-Workout', protein: 'Protein', creatine: 'Creatine', amino: 'Amino & BCAA',
    massgainer: 'Mass Gainers', electrolyte: 'Electrolytes', fatburner: 'Fat Burners',
    health: 'Health & Wellness', other: 'Energy Drinks'
  };
  var PREFERS_REDUCED_MOTION = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var MAX_SELECT = 4;
  var MIN_SELECT = 2;

  // Known ingredient-naming variants (confirmed live during research) -- NOT fuzzy matching,
  // a fixed lookup table so the diff engine can show a transparent "AKA X, Y" note under a
  // canonical row label whenever compared products' own labels used different names for it.
  var INGREDIENT_ALIASES = {
    arginine: ['L-Arginine', 'Arginine Nitrate', 'Nitrosigine', 'Arginine Alpha-Ketoglutarate', 'AAKG'],
    creatine: ['Creatine', 'Creatine Monohydrate', 'Creatine Blend'],
    citrulline: ['L-Citrulline', 'Citrulline Malate'],
    beta_alanine: ['Beta-Alanine', 'Beta Alanine'],
    alpha_gpc: ['Alpha GPC', 'Alpha-GPC'],
    agmatine: ['Agmatine', 'Agmatine Sulfate'],
    betaine: ['Betaine', 'Betaine Anhydrous'],
    glutamine: ['L-Glutamine', 'HPLC-tested L-Glutamine', 'Micronised L-Glutamine'],
    tyrosine: ['L-Tyrosine', 'Tyrosine']
  };

  // Same cert-tag detection as snippets/nzs-tested-badge.liquid, kept in sync manually.
  var CERT_TAGS = [
    { match: ['cert:informed-sport', 'informed sport'], name: 'Informed Sport' },
    { match: ['cert:informed-choice', 'informed choice'], name: 'Informed Choice' },
    { match: ['cert:nsf-sport', 'nsf-sport', 'nsf certified'], name: 'NSF Certified for Sport' },
    { match: ['cert:hasta'], name: 'HASTA' }
  ];

  var root = document.querySelector('[data-nzsc-root]');
  if (!root) return;

  var panel = root.querySelector('[data-nzsc-panel]');
  var stickybar = root.querySelector('[data-nzsc-stickybar]');
  var stickycount = root.querySelector('[data-nzsc-stickycount]');
  var capmsg = root.querySelector('[data-nzsc-capmsg]');
  var compareBtn = root.querySelector('[data-nzsc-compare]');
  var backBtn = root.querySelector('[data-nzsc-back]');
  var resultsEl = root.querySelector('[data-nzsc-results]');

  var META = {};
  try { META = JSON.parse(document.getElementById('nzs-compare-meta').textContent) || {}; } catch (e) { META = {}; }

  var HANDLE_TO_ID = {};
  Object.keys(META).forEach(function (id) { HANDLE_TO_ID[META[id].handle] = id; });

  var INGREDIENT_DICT = null; // { catSlug: [{key,label,unit,div,max,step}, ...] }, captured once
  var activeCat = null;
  var activeSub = null; // NZS-CMP-SUBCATS: child-collection handle currently shown (null = whole category)
  var pendingJumpId = null; // NZS-CMP-SUBCATS: jump target queued behind a grid re-load
  var selected = {}; // id -> { id, title, imgSrc }
  var fetchToken = 0; // guards against a stale fetch resolving after a newer tab switch

  var params = new URLSearchParams(window.location.search);
  var initialCat = params.get('cat');
  var initialFrom = params.get('from');
  var initialSub = params.get('sub'); // NZS-CMP-SUBCATS: child collection to pre-open

  // the site's infinity loader (snippets/nzs-loader.liquid), rendered once as a <template> in the
  // section and cloned per loading state -- falls back to plain text if the template is missing.
  var loaderTpl = root.querySelector('[data-nzsc-loader-tpl]');
  function loadingNode(text) {
    if (loaderTpl && loaderTpl.content) {
      var node = loaderTpl.content.cloneNode(true);
      var txt = node.querySelector('[data-nzsc-loading-txt]');
      if (txt) txt.textContent = text;
      return node;
    }
    var p = document.createElement('p');
    p.className = 'nzsc__placeholder';
    p.textContent = text;
    return p;
  }
  function showLoading(el, text) {
    el.innerHTML = '';
    el.appendChild(loadingNode(text));
  }

  /* ---------- step navigation (copied pattern from nzs-stack-checkout.js) ---------- */
  function goStep(n) {
    root.querySelectorAll('[data-step]').forEach(function (s) {
      s.classList.toggle('is-active', s.getAttribute('data-step') === String(n));
    });
    // the sticky bar belongs to the step-1 picker only -- don't leave it floating over results
    stickybar.hidden = n !== 1 || Object.keys(selected).length === 0;
    closeTray();
    window.scrollTo({ top: 0, behavior: PREFERS_REDUCED_MOTION ? 'auto' : 'smooth' });
  }

  /* ---------- category tabs ---------- */
  function markActiveTab(cat) {
    root.querySelectorAll('[data-nzsc-tab]').forEach(function (b) {
      var isActive = b.getAttribute('data-nzsc-tab') === cat;
      b.classList.toggle('is-active', isActive);
      b.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    /* NZS-CMP-TABSCROLL: keep the active tab in view (deep links land past the fold on mobile) */
    var bar = root.querySelector('.nzsc__tabs');
    var act = bar && bar.querySelector('.nzsc__tab.is-active');
    if (act) bar.scrollTo({ left: act.offsetLeft - (bar.clientWidth - act.offsetWidth) / 2,
      behavior: PREFERS_REDUCED_MOTION ? 'auto' : 'smooth' });
  }

  function clearSelection() {
    selected = {};
    renderStickyBar();
  }

  function loadGrid(handle, opts) {
    opts = opts || {};
    var myToken = ++fetchToken;
    showLoading(panel, 'Loading ' + (opts.label || CAT_LABEL[activeCat] || ''));

    // '?view=nzs-full' (templates/collection.nzs-full.json: render_window 0, no layout) returns
    // EVERY card fully rendered -- zero data-nzs-stub skeletons. The default view ships ~175 of
    // 211 cards as stubs which nzs-filters.js upgrades via fetch(location.pathname+'?view=nzs-full')
    // -- but on THIS page location.pathname is /pages/compare, so that upgrade fetch can never
    // return collection cards and the skeletons would sit unhydrated forever. Works for child
    // collections too -- the nzs-full view template renders for any collection handle.
    fetch('/collections/' + handle + '?view=nzs-full', { headers: { 'X-Requested-With': 'fetch' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        if (myToken !== fetchToken) return; // a later switch already superseded this fetch
        injectCategoryPage(html, activeCat);
        if (opts.preselectHandle) preselectFromHandle(opts.preselectHandle);
        if (pendingJumpId) {
          var pj = pendingJumpId; pendingJumpId = null;
          // a beat later so Alpine/nzsFilters finishes its initial layout pass first (a raw
          // sync jump can catch cards before offsetParent is set). setTimeout, NOT rAF --
          // rAF never fires in a backgrounded tab, which would strand the jump.
          setTimeout(function () { jumpToCard(pj); }, 60);
        }
      })
      .catch(function (err) {
        if (myToken !== fetchToken) return;
        pendingJumpId = null;
        panel.innerHTML = '<p class="nzsc__placeholder">Could not load this category.</p>' +
          '<div class="nzsc__retrywrap"><button type="button" class="nzsc__retry" data-nzsc-retry>Try again</button></div>';
        var retryBtn = panel.querySelector('[data-nzsc-retry]');
        if (retryBtn) retryBtn.addEventListener('click', function () { loadGrid(handle, opts); });
        console.error('[nzs-compare]', err);
      });
  }

  function switchCategory(cat, opts) {
    opts = opts || {};
    if (cat === activeCat && !opts.force) return;
    var changingCategory = activeCat !== null && activeCat !== cat;
    activeCat = cat;
    markActiveTab(cat);
    if (changingCategory) clearSelection();
    setActiveSub(opts.sub || null);
    var label = opts.sub ? (subLabel(opts.sub) || CAT_LABEL[cat]) : CAT_LABEL[cat];
    loadGrid(opts.sub || CAT_HANDLE[cat], { preselectHandle: opts.preselectHandle, label: label });
  }

  /* NZS-CMP-SUBCATS ---------- sub-collection tiles (rows are static Liquid, one per category
     with children; the row matching the active tab is shown, others stay hidden) ---------- */
  function setActiveSub(subHandle) {
    activeSub = subHandle || null;
    root.querySelectorAll('[data-nzsc-subcats-for]').forEach(function (row) {
      var mine = row.getAttribute('data-nzsc-subcats-for') === activeCat;
      row.hidden = !mine;
      if (mine) row.querySelectorAll('[data-nzsc-sub]').forEach(function (t) {
        var on = (t.getAttribute('data-nzsc-sub') || '') === (activeSub || '');
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    });
  }

  function subLabel(subHandle) {
    var t = root.querySelector('[data-nzsc-sub="' + subHandle + '"] .nzs-subc__name');
    return t ? t.textContent.trim() : '';
  }

  function switchSub(subHandle) {
    subHandle = subHandle || null;
    if (subHandle === activeSub) return;
    setActiveSub(subHandle);
    // deliberately NO clearSelection() -- picks persist across sub-switches within a category
    // (Blake's choice: cross-sub compares like whey vs plant are legitimate)
    loadGrid(subHandle || CAT_HANDLE[activeCat], { label: subHandle ? subLabel(subHandle) : CAT_LABEL[activeCat] });
  }

  root.querySelectorAll('[data-nzsc-sub]').forEach(function (tile) {
    tile.addEventListener('click', function () { switchSub(tile.getAttribute('data-nzsc-sub') || null); });
  });

  function injectCategoryPage(html, cat) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    // The Alpine x-data="nzsFilters()" scope lives on the .nzs-collection wrapper itself, NOT on
    // .nzs-toolbar/.nzs-layout individually -- confirmed live (sections/nzs-collection.liquid):
    // <div class="nzs-collection ..." x-data="nzsFilters()" data-active-cat="..." data-shop-all="...">
    //   {% render 'nzs-filter-config' %} <script id="nzs-subcats"> <div class="nzs-toolbar"> <div class="nzs-layout">
    // Importing toolbar+layout as loose siblings (the original version of this function) left them
    // with no backing Alpine component at all, so the drawer/sort/density/filters would all be dead.
    // Importing the WHOLE wrapper as one unit brings the x-data root + both config scripts + toolbar
    // + layout together, correctly scoped, in one atomic append.
    var wrap = doc.querySelector('.nzs-collection');
    var cfgScript = doc.getElementById('nzs-filter-config'); // read only, for INGREDIENT_DICT capture below

    if (!wrap) {
      panel.innerHTML = '<p class="nzsc__placeholder">No products found in this category.</p>';
      return;
    }

    if (!INGREDIENT_DICT && cfgScript) {
      try { INGREDIENT_DICT = (JSON.parse(cfgScript.textContent).categories) || {}; } catch (e) { INGREDIENT_DICT = {}; }
    }

    panel.innerHTML = '';
    panel.appendChild(document.importNode(wrap, true));

    // Alpine v3 auto-initializes newly-added x-data roots via its own MutationObserver; calling
    // initTree explicitly guarantees it regardless of observer timing, and Alpine tracks already-
    // walked elements internally so this is safe even if the observer also fires for the same node.
    try { if (window.Alpine && window.Alpine.initTree) window.Alpine.initTree(panel); } catch (e) { /* no-op */ }

    decorateCards();
  }

  /* ---------- decorate the real, injected cards with a compare-select checkbox ---------- */
  function decorateCards() {
    panel.querySelectorAll('[data-nzs-card]:not([data-nzsc-decorated])').forEach(function (card) {
      card.setAttribute('data-nzsc-decorated', '1');
      var id = card.getAttribute('data-pid');
      // NEVER append the tag inside the card's <a> -- stopPropagation blocks JS handlers but NOT
      // the anchor's native navigation, so a click on the tag would start navigating to the PDP
      // (firing the site's nzs-skeleton.js nav loader) before the checkbox toggle lands. Anchor to
      // the card element itself instead: it's position:static in the theme, so force relative --
      // this is also what makes the absolute-positioned tag stick to THIS card rather than escaping
      // to some positioned ancestor further up the page.
      if (getComputedStyle(card).position === 'static') card.style.position = 'relative';
      var wrap = document.createElement('label');
      wrap.className = 'nzsc__pick';
      wrap.innerHTML = '<input type="checkbox" class="nzsc__pick-input"><span class="nzsc__pick-box"></span><span class="nzsc__pick-txt">Compare</span>';
      var input = wrap.querySelector('input');
      input.checked = !!selected[id];
      // re-injections (sub-switches) must restore the green selected border too, not just the tick
      if (input.checked) card.classList.add('nzsc__card--selected');
      // still stop bubbling so card-level click handlers (quick-add etc.) don't also fire
      wrap.addEventListener('click', function (e) { e.stopPropagation(); });
      input.addEventListener('click', function (e) { e.stopPropagation(); });
      input.addEventListener('change', function (e) { e.stopPropagation(); toggleSelect(card, id, input); });
      card.appendChild(wrap);
    });
  }

  function toggleSelect(card, id, input) {
    if (input.checked) {
      if (Object.keys(selected).length >= MAX_SELECT) {
        input.checked = false;
        showCapMessage();
        return;
      }
      var img = card.querySelector('img');
      selected[id] = {
        id: id,
        title: card.getAttribute('data-title') || '',
        imgSrc: img ? (img.currentSrc || img.src) : ''
      };
      card.classList.add('nzsc__card--selected');
    } else {
      delete selected[id];
      card.classList.remove('nzsc__card--selected');
    }
    renderStickyBar();
  }

  var CAP_DEFAULT = capmsg ? capmsg.textContent : '';
  function flashMsg(text) {
    capmsg.textContent = text || CAP_DEFAULT;
    capmsg.hidden = false;
    window.clearTimeout(showCapMessage._t);
    showCapMessage._t = window.setTimeout(function () {
      capmsg.hidden = true;
      capmsg.textContent = CAP_DEFAULT;
    }, 2200);
  }
  function showCapMessage() { flashMsg(CAP_DEFAULT); }

  function preselectFromHandle(handle) {
    var id = HANDLE_TO_ID[handle];
    if (!id) return;
    var card = panel.querySelector('[data-nzs-card][data-pid="' + id + '"]');
    if (!card) return;
    var input = card.querySelector('.nzsc__pick-input');
    if (input && !input.checked) { input.checked = true; toggleSelect(card, id, input); }
    var tag = document.createElement('span');
    tag.className = 'nzsc__viewing';
    tag.textContent = 'Viewing this product';
    card.appendChild(tag);
  }

  function renderStickyBar() {
    var n = Object.keys(selected).length;
    stickybar.hidden = n === 0;
    stickycount.textContent = n + ' selected';
    compareBtn.disabled = n < MIN_SELECT;
    compareBtn.textContent = 'Compare Selected (' + n + ')';
    if (n === 0) closeTray();
    else if (!tray.hidden) renderTray(); // keep an open tray in sync as picks change
  }

  /* ---------- selection tray: "N selected" toggles a panel SURFACING ALL selected products at
     once (thumb + name + remove), instead of scroll-cycling one at a time. Clicking a name still
     jumps to that card in the grid; the X unticks it. ---------- */
  var tray = root.querySelector('[data-nzsc-tray]');

  function closeTray() {
    tray.hidden = true;
    stickycount.setAttribute('aria-expanded', 'false');
  }

  function jumpToCard(id) {
    var card = panel.querySelector('[data-nzs-card][data-pid="' + id + '"]');
    if (!card && activeSub) {
      // picked under a different sub-collection -- return to the whole category (picks persist),
      // then jump once the grid lands (loadGrid drains pendingJumpId after injection)
      pendingJumpId = id;
      switchSub(null);
      return;
    }
    if (!card) return;
    if (!card.offsetParent) {
      // present but hidden by active filters -- surface the grid instead of a dead click
      panel.scrollIntoView({ behavior: PREFERS_REDUCED_MOTION ? 'auto' : 'smooth', block: 'start' });
      flashMsg('Hidden by your current filters');
      return;
    }
    card.scrollIntoView({ behavior: PREFERS_REDUCED_MOTION ? 'auto' : 'smooth', block: 'center' });
    card.classList.remove('nzsc__card--flash');
    void card.offsetWidth; // restart the flash if the same card is hit twice
    card.classList.add('nzsc__card--flash');
    setTimeout(function () { card.classList.remove('nzsc__card--flash'); }, 1300);
  }

  function unselectById(id) {
    var card = panel.querySelector('[data-nzs-card][data-pid="' + id + '"]');
    var input = card ? card.querySelector('.nzsc__pick-input') : null;
    if (input) {
      input.checked = false;
      toggleSelect(card, id, input);
    } else {
      // card no longer in the DOM (shouldn't happen within a category) -- drop the entry directly
      delete selected[id];
      renderStickyBar();
    }
  }

  function renderTray() {
    var ids = Object.keys(selected);
    tray.innerHTML = '';
    ids.forEach(function (id) {
      var it = selected[id];
      var row = document.createElement('div');
      row.className = 'nzsc__tray-row';

      var jump = document.createElement('button');
      jump.type = 'button';
      jump.className = 'nzsc__tray-jump';
      jump.title = 'Jump to this product';
      if (it.imgSrc) {
        var img = document.createElement('img');
        img.src = it.imgSrc; img.alt = ''; img.loading = 'lazy';
        jump.appendChild(img);
      }
      var name = document.createElement('span');
      name.textContent = it.title;
      jump.appendChild(name);
      jump.addEventListener('click', function () { closeTray(); jumpToCard(id); });

      var rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'nzsc__tray-rm';
      rm.setAttribute('aria-label', 'Remove ' + it.title + ' from comparison');
      rm.innerHTML = '<svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M5 15L15 5M5 5L15 15"></path></svg>';
      rm.addEventListener('click', function () { unselectById(id); });

      row.appendChild(jump);
      row.appendChild(rm);
      tray.appendChild(row);
    });
  }

  stickycount.addEventListener('click', function () {
    if (!Object.keys(selected).length) return;
    if (tray.hidden) {
      renderTray();
      tray.hidden = false;
      stickycount.setAttribute('aria-expanded', 'true');
    } else {
      closeTray();
    }
  });

  // close the tray on a click anywhere outside the sticky bar. The isConnected guard matters:
  // clicking the tray's own remove button re-renders the tray, DETACHING the clicked node before
  // this listener runs -- contains() would then be false and wrongly close the tray mid-use.
  document.addEventListener('click', function (e) {
    if (!tray.hidden && e.target.isConnected && !stickybar.contains(e.target)) closeTray();
  });

  root.querySelectorAll('[data-nzsc-tab]').forEach(function (btn) {
    btn.addEventListener('click', function () { switchCategory(btn.getAttribute('data-nzsc-tab')); });
  });

  /* NZS-CMP-TABSCROLL: the tab row overflows on small screens with the scrollbar hidden --
     add edge-fade cues, mouse drag-to-scroll (touch keeps native panning), and centre the
     pre-selected tab so a deep-linked category is never off-screen. */
  var tabbar = root.querySelector('.nzsc__tabs');
  if (tabbar) {
    var updateFades = function () {
      var max = tabbar.scrollWidth - tabbar.clientWidth;
      tabbar.classList.toggle('is-fade-l', tabbar.scrollLeft > 4);
      tabbar.classList.toggle('is-fade-r', tabbar.scrollLeft < max - 4);
    };
    tabbar.addEventListener('scroll', updateFades, { passive: true });
    window.addEventListener('resize', updateFades, { passive: true });
    updateFades();

    /* NZS-CMP-TABCHEV: arrows page the row by ~70% of a screenful */
    root.querySelectorAll('[data-nzsc-tabnav]').forEach(function (nav) {
      nav.addEventListener('click', function () {
        tabbar.scrollBy({
          left: parseInt(nav.getAttribute('data-nzsc-tabnav'), 10) * Math.round(tabbar.clientWidth * 0.7),
          behavior: PREFERS_REDUCED_MOTION ? 'auto' : 'smooth'
        });
      });
    });

    var dragX = null, dragStartLeft = 0, dragged = false;
    tabbar.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      dragX = e.clientX; dragStartLeft = tabbar.scrollLeft; dragged = false;
    });
    window.addEventListener('pointermove', function (e) {
      if (dragX == null) return;
      var dx = e.clientX - dragX;
      if (Math.abs(dx) > 4) dragged = true;
      if (dragged) { tabbar.classList.add('is-dragging'); tabbar.scrollLeft = dragStartLeft - dx; }
    });
    window.addEventListener('pointerup', function () {
      dragX = null; tabbar.classList.remove('is-dragging');
    });
    /* a drag must never fire the tab underneath the pointer on release */
    tabbar.addEventListener('click', function (e) {
      if (dragged) { e.stopPropagation(); e.preventDefault(); dragged = false; }
    }, true);
  }

  compareBtn.addEventListener('click', function () {
    if (compareBtn.disabled) return;
    buildResults(Object.keys(selected));
    goStep(2);
  });

  backBtn.addEventListener('click', function () { goStep(1); });

  /* ---------- diff / highlight engine ---------- */
  function parseCoreIngredients(core) {
    if (!core) return [];
    return String(core).split('\n').map(function (line) { return line.trim(); }).filter(Boolean).map(function (line) {
      var parts = line.split('|');
      return { name: (parts[0] || '').trim(), amount: (parts[1] || '').trim(), percent: (parts[2] || '').trim() };
    });
  }

  function titleCase(s) {
    return s.replace(/\w\S*/g, function (t) { return t.charAt(0).toUpperCase() + t.substr(1).toLowerCase(); });
  }

  function buildDiffRows(products, ingredientDict, cat) {
    var dictList = (ingredientDict && ingredientDict[cat] && ingredientDict[cat].ingredients) || [];
    var slugSet = {};
    products.forEach(function (p) {
      var ing = (p.facets && p.facets.ing) || {};
      Object.keys(ing).forEach(function (slug) { slugSet[slug] = true; });
    });

    var rows = Object.keys(slugSet).map(function (slug) {
      var dict = dictList.filter(function (i) { return i.key === slug; })[0];
      var label = dict ? dict.label : titleCase(slug.replace(/_/g, ' '));
      var unit = dict ? dict.unit : 'mg';
      var div = dict ? dict.div : 1;

      var aliases = INGREDIENT_ALIASES[slug] || [];
      var akaFound = {};
      products.forEach(function (p) {
        parseCoreIngredients(p.core).forEach(function (r) {
          var norm = r.name.toLowerCase();
          if (norm !== label.toLowerCase() && aliases.some(function (a) { return a.toLowerCase() === norm; })) {
            akaFound[r.name] = true;
          }
        });
      });

      var cells = products.map(function (p) {
        var raw = p.facets && p.facets.ing ? p.facets.ing[slug] : null;
        return raw == null ? { present: false } : { present: true, raw: raw, display: raw / div };
      });

      var presentVals = cells.filter(function (c) { return c.present; }).map(function (c) { return c.raw; });
      var sharedCount = presentVals.length;
      var sortedDesc = presentVals.slice().sort(function (a, b) { return b - a; });
      var max = sortedDesc.length ? sortedDesc[0] : 0;
      var secondMax = sortedDesc.length > 1 ? sortedDesc[1] : max;
      var allEqual = presentVals.every(function (v) { return v === presentVals[0]; });

      cells.forEach(function (c) {
        if (!c.present) return;
        c.isHighest = sharedCount > 1 && !allEqual && c.raw === max;
        // winner shows its margin over the next-best (positive); every other cell shows how far
        // below the max it is (negative) -- both rendered as a plain positive amount in the UI.
        if (sharedCount > 1 && !allEqual) {
          c.delta = c.isHighest ? (max - secondMax) : (c.raw - max);
        } else {
          c.delta = null;
        }
      });

      return { slug: slug, label: label, unit: unit, div: div, aka: Object.keys(akaFound), cells: cells, sharedCount: sharedCount, allEqual: allEqual };
    });

    rows.sort(function (a, b) {
      var aShared = a.sharedCount > 1, bShared = b.sharedCount > 1;
      if (aShared !== bShared) return aShared ? -1 : 1;
      return a.label.localeCompare(b.label);
    });
    return rows;
  }

  function hasAnyCert(tags) {
    tags = (tags || []).join('||').toLowerCase();
    return CERT_TAGS.filter(function (c) { return c.match.some(function (m) { return tags.indexOf(m) > -1; }); });
  }

  /* ---------- results (fetches live commerce data, joins with the metafield blob) ---------- */
  function buildResults(ids) {
    showLoading(resultsEl, 'Loading comparison');
    var lookups = ids.map(function (id) {
      var meta = META[id] || {};
      return fetch('/products/' + meta.handle + '.js').then(function (r) { return r.json(); }).then(function (live) {
        return {
          id: id, handle: meta.handle, core: meta.core, facets: meta.facets, servings: meta.servings,
          title: live.title, price: live.price, compareAtPrice: live.compare_at_price,
          image: live.featured_image || (live.images && live.images[0]), available: live.available, tags: live.tags
        };
      });
    });

    Promise.all(lookups).then(function (products) {
      renderResults(products);
    }).catch(function (err) {
      resultsEl.innerHTML = '<p class="nzsc__placeholder">Could not load comparison data.</p>' +
        '<div class="nzsc__retrywrap"><button type="button" class="nzsc__retry" data-nzsc-retry>Try again</button></div>';
      var retryBtn = resultsEl.querySelector('[data-nzsc-retry]');
      if (retryBtn) retryBtn.addEventListener('click', function () { buildResults(ids); });
      console.error('[nzs-compare]', err);
    });
  }

  function money(cents) { return '$' + (cents / 100).toFixed(2); }

  function renderResults(products) {
    var cat = activeCat;
    var rows = buildDiffRows(products, INGREDIENT_DICT, cat);
    // all rows share ONE outer horizontal scroller (.nzsc__scrollwrap, see nzs-compare.css) so on
    // mobile every row's columns move together -- each individual .nzsc__resgrid does NOT scroll
    // on its own, or the header/ingredient/meta rows would desync when swiped independently.
    var html = '<div class="nzsc__scrollwrap">';

    html += '<div class="nzsc__resgrid" style="--cols:' + products.length + '">';
    html += '<div class="nzsc__rescol nzsc__rescol--label"></div>';
    products.forEach(function (p) {
      var certs = hasAnyCert(p.tags);
      html += '<div class="nzsc__rescol nzsc__reshead">' +
        (p.image ? '<img src="' + p.image + '" alt="" loading="lazy">' : '') +
        '<div class="nzsc__resttl">' + p.title + '</div>' +
        '<div class="nzsc__resprice">' + money(p.price) +
        (p.compareAtPrice > p.price ? ' <s>' + money(p.compareAtPrice) + '</s>' : '') + '</div>' +
        (certs.length ? '<div class="nzsc__rescert">Certified: ' + certs.map(function (c) { return c.name; }).join(', ') + '</div>' : '') +
        '</div>';
    });
    html += '</div>';

    rows.forEach(function (row) {
      html += '<div class="nzsc__resgrid nzsc__resrow" style="--cols:' + products.length + '">';
      html += '<div class="nzsc__rescol nzsc__reslabel"><span class="nzsc__reslabelmain">' + row.label + ' (' + row.unit + ' per serving)</span>' +
        (row.aka.length ? '<span class="nzsc__resaka">AKA ' + row.aka.join(', ') + '</span>' : '') + '</div>';
      row.cells.forEach(function (c) {
        var cls = 'nzsc__rescol nzsc__rescell';
        var mainText, subText = '';
        if (!c.present) {
          mainText = 'Not listed on this product’s label';
          cls += ' nzsc__rescell--absent';
        } else {
          mainText = c.display + row.unit;
          if (row.sharedCount <= 1) { subText = 'Only listed on this product'; }
          else if (row.allEqual) { subText = 'Same amount across all selected'; }
          // only the winning cell gets a delta note ("Xmg higher") -- the lower cells just show
          // their amount, no "Xmg less" (the green highlight + the winner's note already say it).
          else if (c.isHighest) { subText = Math.abs(c.delta / row.div).toFixed(row.div > 1 ? 1 : 0) + row.unit + ' higher'; cls += ' nzsc__rescell--high'; }
        }
        // main amount on one line, the comparison note underneath in thinner text
        html += '<div class="' + cls + '"><span class="nzsc__rescell-val">' + mainText + '</span>' +
          (subText ? '<span class="nzsc__rescell-delta">' + subText + '</span>' : '') + '</div>';
      });
      html += '</div>';
    });

    // neutral meta rows: price/serving, net weight, servings/container
    html += metaRow('Price per serving', products, function (p) {
      return p.servings > 0 ? money(Math.round(p.price / p.servings)) : '—';
    });
    html += metaRow('Servings per container', products, function (p) { return p.servings || '—'; });
    html += metaRow('Net weight', products, function (p) {
      var g = p.facets && p.facets.net_g;
      return g ? g + 'g' : '—';
    });

    html += '</div>'; // .nzsc__scrollwrap
    resultsEl.innerHTML = html;
  }

  function metaRow(label, products, fn) {
    var html = '<div class="nzsc__resgrid nzsc__resrow nzsc__resrow--meta" style="--cols:' + products.length + '">';
    html += '<div class="nzsc__rescol nzsc__reslabel"><span class="nzsc__reslabelmain">' + label + '</span></div>';
    products.forEach(function (p) { html += '<div class="nzsc__rescol nzsc__rescell">' + fn(p) + '</div>'; });
    html += '</div>';
    return html;
  }

  /* ---------- initial load: preselect category + product from ?cat=&from= (+ ?sub= child
     collection, validated against the rendered tile rows) ---------- */
  if (initialCat && CAT_HANDLE[initialCat]) {
    var subOk = initialSub && root.querySelector(
      '[data-nzsc-subcats-for="' + initialCat + '"] [data-nzsc-sub="' + initialSub + '"]');
    switchCategory(initialCat, { preselectHandle: initialFrom || null, sub: subOk ? initialSub : null });
  }
})();
